import { z } from 'zod';

export const FUNDING_REWARD_TYPES = ['DIGITAL', 'PHYSICAL', 'EXPERIENCE'] as const;
// D-1 — 펀딩 카테고리 (SF 필터 ALBUM/MD/LIGHT_STICK/ETC 와 동일 enum, settlement BE 계약).
export const FUNDING_CATEGORIES = ['ALBUM', 'MD', 'LIGHT_STICK', 'ETC'] as const;
export const FUNDING_STATUSES = [
  'DRAFT',
  'ACTIVE',
  'SUCCEEDED',
  'FAILED',
  'CANCELLED',
  'SETTLED',
] as const;
export const PLEDGE_STATUSES = [
  'PENDING',
  'PAID',
  'CANCELLED',
  'REFUNDED',
  'SETTLED',
] as const;

const DATE_TIME_RE = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/;
const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;
const KRW_DECIMAL_RE = /^\d+(?:\.\d{1,2})?$/;
const UUID_RE =
  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/;
const MIN_RANGE_HOURS = 24;

const amountString = z
  .string()
  .trim()
  .refine((v) => KRW_DECIMAL_RE.test(v) && Number(v) > 0, {
    message: 'funding.errors.goalAmountInvalid',
  });

const minPledgeAmountString = z
  .string()
  .trim()
  .refine((v) => KRW_DECIMAL_RE.test(v) && Number(v) > 0, {
    message: 'funding.errors.minPledgeAmountInvalid',
  });

const optionalString = (max: number, message: string) =>
  z.string().trim().max(max, { message }).optional().or(z.literal(''));

export const fundingRewardSchema = z.object({
  tierName: z
    .string()
    .trim()
    .min(1, { message: 'funding.errors.tierNameRequired' })
    .max(100, { message: 'funding.errors.tierNameTooLong' }),
  description: optionalString(2000, 'funding.errors.rewardDescriptionTooLong'),
  type: z.enum(FUNDING_REWARD_TYPES),
  minPledgeAmount: minPledgeAmountString,
  maxQuantity: z
    .string()
    .trim()
    .refine(
      (v) => v === '' || (/^\d+$/.test(v) && Number(v) >= 1),
      { message: 'funding.errors.maxQuantityInvalid' },
    )
    .optional()
    .or(z.literal('')),
  estimatedDeliveryAt: z
    .string()
    .trim()
    .refine((v) => v === '' || DATE_RE.test(v), {
      message: 'funding.errors.estimatedDeliveryInvalid',
    })
    .optional()
    .or(z.literal('')),
  sortOrder: z.coerce
    .number()
    .int()
    .min(0, { message: 'funding.errors.sortOrderInvalid' })
    .optional(),
});

export type FundingRewardValues = z.infer<typeof fundingRewardSchema>;

export const emptyFundingRewardValues = (): FundingRewardValues => ({
  tierName: '',
  description: '',
  type: 'PHYSICAL',
  minPledgeAmount: '',
  maxQuantity: '',
  estimatedDeliveryAt: '',
  sortOrder: 0,
});

const isFutureRange = (start: string, end: string): boolean => {
  const s = Date.parse(`${start}:00Z`);
  const e = Date.parse(`${end}:00Z`);
  if (Number.isNaN(s) || Number.isNaN(e)) return false;
  return e - s >= MIN_RANGE_HOURS * 60 * 60 * 1000;
};

export const fundingCreateSchema = z
  .object({
    vendorId: optionalString(32, 'funding.errors.vendorIdTooLong'),
    artistId: z
      .string()
      .trim()
      .refine((v) => v === '' || UUID_RE.test(v), {
        message: 'funding.errors.artistIdInvalid',
      })
      .optional()
      .or(z.literal('')),
    title: z
      .string()
      .trim()
      .min(1, { message: 'funding.errors.titleRequired' })
      .max(200, { message: 'funding.errors.titleTooLong' }),
    summary: optionalString(500, 'funding.errors.summaryTooLong'),
    description: optionalString(20000, 'funding.errors.descriptionTooLong'),
    coverImageUrl: z
      .string()
      .trim()
      .refine((v) => v === '' || /^https?:\/\//i.test(v), {
        message: 'funding.errors.coverImageUrlInvalid',
      })
      .optional()
      .or(z.literal('')),
    category: z.enum(FUNDING_CATEGORIES).optional().or(z.literal('')),
    goalAmount: amountString,
    startAt: z
      .string()
      .trim()
      .regex(DATE_TIME_RE, { message: 'funding.errors.dateFormat' }),
    endAt: z
      .string()
      .trim()
      .regex(DATE_TIME_RE, { message: 'funding.errors.dateFormat' }),
    refundPolicy: optionalString(2000, 'funding.errors.refundPolicyTooLong'),
    internalNote: optionalString(2000, 'funding.errors.internalNoteTooLong'),
    detailImageUrls: z.array(z.string().trim().min(1)).max(10).optional(),
    initialRewards: z.array(fundingRewardSchema).max(20).optional(),
  })
  .superRefine((data, ctx) => {
    if (!DATE_TIME_RE.test(data.startAt) || !DATE_TIME_RE.test(data.endAt)) return;
    const s = Date.parse(`${data.startAt}:00Z`);
    const e = Date.parse(`${data.endAt}:00Z`);
    if (Number.isNaN(s) || Number.isNaN(e)) return;
    if (e <= s) {
      ctx.addIssue({
        code: 'custom',
        path: ['endAt'],
        message: 'funding.errors.endBeforeStart',
      });
      return;
    }
    if (!isFutureRange(data.startAt, data.endAt)) {
      ctx.addIssue({
        code: 'custom',
        path: ['endAt'],
        message: 'funding.errors.rangeTooShort',
      });
    }
  });

export type FundingCreateValues = z.infer<typeof fundingCreateSchema>;

export const emptyFundingCreateValues = (): FundingCreateValues => ({
  vendorId: '',
  artistId: '',
  title: '',
  summary: '',
  description: '',
  category: '',
  coverImageUrl: '',
  goalAmount: '',
  startAt: '',
  endAt: '',
  refundPolicy: '',
  internalNote: '',
  detailImageUrls: [],
  initialRewards: [],
});

export const fundingUpdateSchema = z
  .object({
    title: z
      .string()
      .trim()
      .min(1, { message: 'funding.errors.titleRequired' })
      .max(200, { message: 'funding.errors.titleTooLong' })
      .optional(),
    summary: optionalString(500, 'funding.errors.summaryTooLong'),
    description: optionalString(20000, 'funding.errors.descriptionTooLong'),
    coverImageUrl: z
      .string()
      .trim()
      .refine((v) => v === '' || /^https?:\/\//i.test(v), {
        message: 'funding.errors.coverImageUrlInvalid',
      })
      .optional()
      .or(z.literal('')),
    category: z.enum(FUNDING_CATEGORIES).optional().or(z.literal('')),
    refundPolicy: optionalString(2000, 'funding.errors.refundPolicyTooLong'),
    internalNote: optionalString(2000, 'funding.errors.internalNoteTooLong'),
    // QA-398 — 시작·종료 일시(KST datetime-local). BE 는 작성중(DRAFT)에서만 바꿀 수 있다.
    startAt: z.string().regex(DATE_TIME_RE, { message: 'funding.errors.dateFormat' }).optional().or(z.literal('')),
    endAt: z.string().regex(DATE_TIME_RE, { message: 'funding.errors.dateFormat' }).optional().or(z.literal('')),
  })
  .refine((d) => Object.values(d).some((v) => v !== undefined && v !== ''), {
    message: 'funding.errors.updateMinOne',
  })
  .refine((d) => !d.startAt || !d.endAt || d.endAt > d.startAt, {
    message: 'funding.errors.endBeforeStart',
    path: ['endAt'],
  });

export type FundingUpdateValues = z.infer<typeof fundingUpdateSchema>;

export const fundingLaunchSchema = z.object({
  note: optionalString(500, 'funding.errors.noteTooLong'),
});

export type FundingLaunchValues = z.infer<typeof fundingLaunchSchema>;

export const fundingCloseSchema = z
  .object({
    forceCancel: z.boolean(),
    cancelReason: optionalString(500, 'funding.errors.cancelReasonTooLong'),
  })
  .superRefine((data, ctx) => {
    if (data.forceCancel) {
      if (!data.cancelReason || data.cancelReason.trim().length === 0) {
        ctx.addIssue({
          code: 'custom',
          path: ['cancelReason'],
          message: 'funding.errors.cancelReasonRequired',
        });
      }
    }
  });

export type FundingCloseValues = z.infer<typeof fundingCloseSchema>;

export const dashboardRangeSchema = z
  .object({
    range: z.enum(['TODAY', 'WEEK_7D', 'MONTH_30D', 'QUARTER_90D', 'CUSTOM']),
    from: z.string().trim().optional().or(z.literal('')),
    to: z.string().trim().optional().or(z.literal('')),
  })
  .superRefine((data, ctx) => {
    if (data.range !== 'CUSTOM') return;
    if (!data.from || !data.to) {
      ctx.addIssue({
        code: 'custom',
        path: ['range'],
        message: 'funding.errors.rangeCustomMissing',
      });
      return;
    }
    const f = Date.parse(data.from);
    const t = Date.parse(data.to);
    if (!Number.isNaN(f) && !Number.isNaN(t) && t <= f) {
      ctx.addIssue({
        code: 'custom',
        path: ['to'],
        message: 'funding.errors.rangeCustomInvalid',
      });
    }
  });

export type DashboardRangeValues = z.infer<typeof dashboardRangeSchema>;
